import type { Alert, AppSettings } from '@convatec/180medical-website/types/app'

interface IAppSettingsState {
  loading: boolean
  alertsLoading: boolean
  appSettings: AppSettings
  alerts: Alert[]
}

export const useAppSettingsStore = defineStore('appSettings', {
  state: (): IAppSettingsState => ({
    loading: false,
    alertsLoading: false,
    appSettings: {
      headerNav: [],
      headerSettings: {},
      loginMenuSettings: {},
      footerSettings: {},
      contactSettings: {},
      contactInfo: {},
      talkdeskSettings: {},
    },
    alerts: [],
  }),
  getters: {
    getAppSettings: (state) => {
      return state.appSettings
    },
    headerNav: (state) => {
      return state.appSettings.headerNav || []
    },
    headerLogo: state => ({
      imageUrl: state.appSettings.headerSettings?.headerLogo?.imageUrl || '/images/180-logo.png',
      imageAltText: state.appSettings.headerSettings?.headerLogo?.imageAltText || '180 Medical Logo',
      width: state.appSettings.headerSettings?.headerLogo?.width,
      height: state.appSettings.headerSettings?.headerLogo?.height,
    }),
    headerSettings: (state) => {
      return state.appSettings.headerSettings || {}
    },
    loginMenuSettings: (state) => {
      return state.appSettings.loginMenuSettings || {}
    },
    footerSettings: (state) => {
      return state.appSettings.footerSettings || {}
    },
    contactSettings: (state) => {
      return state.appSettings.contactSettings || {}
    },
    contactInfo: (state) => {
      return state.appSettings.contactInfo || {}
    },
    talkdeskSettings: (state) => {
      return state.appSettings.talkdeskSettings || {}
    },
  },
  actions: {
    init() {
      this.alerts = []
      this.fetchAlerts()
    },
    async fetchNavigation() {
      this.loading = true
      try {
        const data: AppSettings = await $fetch<AppSettings>('/api/app-settings')
        this.appSettings = data
        this.loading = false

        return data
      }
      catch (error) {
        console.error('Failed to fetch appsettings:', error)
      }
      finally {
        this.loading = false
      }
    },

    async fetchAlerts() {
      this.alertsLoading = true
      this.alerts = []
      try {
        const alerts: Alert[] = await $fetch<Alert[]>('/api/app-alerts')

        const filteredAlerts = alerts.filter((alert) => {
          return this.isAlertActive(alert)
        })

        this.alerts = filteredAlerts.map(alert => ({
          ...alert,
        }))
      }
      catch (error) {
        console.error('Failed to fetch alerts:', error)
      }
      finally {
        this.alertsLoading = false
      }
    },

    isAlertActive(alert: Alert): boolean {
      const alertKey = alert.key
      const cookie = useCookie(`alert-${alertKey}`)
      const dismissedTime = cookie.value ? Number.parseInt(cookie.value as string) : null

      if (!dismissedTime || !alert.dismissDuration)
        return true

      if (alert.dismissDuration) {
        const now = Date.now()
        const dismissDurationMs = alert.dismissDuration * 24 * 60 * 60 * 1000 // Convert days to milliseconds
        return now - dismissedTime > dismissDurationMs
      }

      return false
    },

    dismissAlert(alert: Alert) {
      const alertKey = alert.key
      const dismissedTime = Date.now()
      const cookieOptions: { maxAge?: number } = {}

      if (alert.dismissDuration) {
        cookieOptions.maxAge = alert.dismissDuration * 24 * 60 * 60 // Convert days to seconds
      }

      const cookie = useCookie(`alert-${alertKey}`, cookieOptions)
      cookie.value = dismissedTime.toString()
      this.alerts = this.alerts.filter(a => a.key !== alert.key)
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAppSettingsStore, import.meta.hot))
}

export type AppSettingsStore = ReturnType<typeof useAppSettingsStore>
